import { useRef, useState } from 'react';
import { IconBank } from '@belong/icons';
import { Button, Text } from '@belong/ui';
import clsx from 'clsx';
import { CoverageRegionModel } from 'src/api/models';
import { TAX_TEXT } from '../../constants';
import { PriceTable } from '../price-table/price-table';
import css from './price-table-preview.module.css';

type Props = {
  title: string;
  subtitle: string;
  // Optional, mirroring `PriceTable`'s own prop below: this only forwards it,
  // and the neighbourhood page resolves its region with a `find`.
  regionId?: CoverageRegionModel['id'];
  showMore: string;
  showLess: string;
  priceTableRef?: React.RefObject<HTMLDivElement>;
  enableToggleExpansion?: boolean;
};

export function PriceTablePreview({
  title,
  subtitle,
  regionId,
  showMore,
  showLess,
  priceTableRef,
  enableToggleExpansion = true,
}: Props): JSX.Element {
  const [isTableCollapsed, setIsTableCollapsed] = useState(
    enableToggleExpansion,
  );
  let tableRef = useRef<HTMLDivElement>(null);
  if (priceTableRef) {
    tableRef = priceTableRef;
  }
  function scrollToPriceTable() {
    const priceTable = tableRef.current;
    if (priceTable) {
      window.scrollBy({
        top: priceTable.getBoundingClientRect().top,
        behavior: 'smooth',
      });
    }
  }

  return (
    <>
      <div ref={tableRef} className="mb-xs md:mb-2xs">
        <Text variant="h2" fontWeight="semibold" className="text-center">
          {title}
        </Text>
      </div>
      <div className="mb-2xl flex justify-center">
        <Text variant="body" className="text-center md:hidden">
          {subtitle}
        </Text>
        <Text variant="h3" className="hidden text-center md:block">
          {subtitle}
        </Text>
      </div>
      <div className="mb-xs relative w-full">
        <div
          className={clsx(
            'duration-hover w-full overflow-hidden transition-all',
            isTableCollapsed
              ? css.priceTableCollapsed
              : css.priceTableNormalHeight,
          )}
        >
          <PriceTable regionId={regionId} />
        </div>
        <div
          className={clsx(
            'h-4xl duration-hover absolute inset-x-0 bottom-0 bg-gradient-to-t from-white transition',
            !isTableCollapsed && 'hidden',
          )}
        />
      </div>
      <div className="flex w-full flex-col items-center md:flex-row-reverse md:justify-between">
        {enableToggleExpansion && (
          <div className="mb-lg self-end md:mb-0">
            <Button
              variant="text"
              size="auto"
              onClick={() => {
                if (!isTableCollapsed) {
                  scrollToPriceTable();
                }
                setIsTableCollapsed(!isTableCollapsed);
              }}
            >
              {isTableCollapsed ? showMore : showLess}
            </Button>
          </div>
        )}
        <div className="flex items-center">
          <div className="mr-2sm md:mr-xs flex-none">
            <IconBank width={17} />
          </div>
          {TAX_TEXT}
        </div>
      </div>
    </>
  );
}
